'use client';

import { useState } from 'react';

import { CompanyPersonSelectFields } from '@/features/contacts/components';
import { ServiceTypeSelect } from '@/features/service-types/components';
import {
  useCreateTaskAssigneeMutation,
  useUpdateTaskAssigneeMutation,
} from '@/features/tasks/api/tasksApi';
import { AssigneeSelect } from '@/features/workspaces/components';
import { SectionTabs, SmartInput } from '@web/ui';
import {
  backendErrorsToFieldErrors,
  getErrorMessage,
  notify,
} from '@web/utils';

import styles from './QuickTaskSubRowForm.module.scss';

import type { CompanyOption } from '@/features/contacts/companies/components/CompanySelectField';
import type { PersonOption } from '@/features/contacts/people/components/PersonSelectField';
import type { Task } from '@/features/tasks/types';
import type { CreateTaskAssigneePayload } from '@/features/tasks/types';
import type { UpdateTaskPayload } from '@/features/tasks/types';

type QuickTaskSubRowFormProps = {
  task: Task;
  onUpdateTask: (args: {
    taskId: string;
    payload: UpdateTaskPayload;
  }) => Promise<unknown>;
};

type ClientMode = 'client' | 'internal';

type FormErrors = Partial<
  Record<
    | 'description'
    | 'company_id'
    | 'person_id'
    | 'assignee_id'
    | 'service_type_id'
    | 'level_of_effort'
    | 'form',
    string
  >
>;

const CONTEXT_SECTIONS = [
  { id: 'client', label: 'Client' },
  { id: 'internal', label: 'Internal' },
] as const;

type TaskPerson = NonNullable<NonNullable<Task['client']>['person']>;

function toPersonName(person: TaskPerson): string {
  const firstName = person?.first_name ?? '';
  const lastName = person?.last_name ?? '';
  const fullName = `${firstName} ${lastName}`.trim();

  return fullName || person?.email || 'Unknown person';
}

function mapBackendPath(path: string): string | null {
  if (path === 'description.0' || path === 'description') return 'description';
  if (path === 'company_contact_id.0' || path === 'company_contact_id')
    return 'company_id';
  if (path === 'person_contact_id.0' || path === 'person_contact_id')
    return 'person_id';
  if (path === 'task_assignee_payloads.0.user_id') return 'assignee_id';
  if (path === 'task_assignee_payloads.0.service_type_id')
    return 'service_type_id';
  if (path === 'task_assignee_payloads.0.level_of_effort')
    return 'level_of_effort';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

function mapAssigneeBackendPath(path: string): string | null {
  if (path === 'user_id.0' || path === 'user_id') return 'assignee_id';
  if (path === 'task_assignee_payloads.0.user_id') return 'assignee_id';
  if (path === 'service_type_id.0' || path === 'service_type_id')
    return 'service_type_id';
  if (path === 'task_assignee_payloads.0.service_type_id')
    return 'service_type_id';
  if (path === 'level_of_effort.0' || path === 'level_of_effort')
    return 'level_of_effort';
  if (path === 'task_assignee_payloads.0.level_of_effort')
    return 'level_of_effort';
  if (path === 'task_assignee_payloads.0') return 'form';
  if (path === 'task_assignee_payloads') return 'form';
  if (path === 'non_field_errors.0') return 'form';

  return path;
}

function getPrimaryAssignee(task: Task) {
  return task.assignees?.[0] ?? null;
}

function getAssigneeServiceTypeId(task: Task) {
  const primaryAssignee = getPrimaryAssignee(task);

  return (
    primaryAssignee?.service_type_id ?? primaryAssignee?.service_type?.id ?? ''
  );
}

function getAssigneeName(task: Task): string {
  const assignee = getPrimaryAssignee(task)?.user;

  if (!assignee) {
    return '';
  }

  const fullName =
    `${assignee.first_name ?? ''} ${assignee.last_name ?? ''}`.trim();

  return fullName || assignee.email || '';
}

function getAssigneeAvatar(task: Task): string | undefined {
  const assignee = getPrimaryAssignee(task)?.user;
  const rawAssignee = assignee as typeof assignee & {
    avatar_url?: string | null;
    avatar?: string | null;
  };

  return rawAssignee?.avatar_url ?? rawAssignee?.avatar ?? undefined;
}

export function QuickTaskSubRowForm({
  task,
  onUpdateTask,
}: QuickTaskSubRowFormProps) {
  const primaryAssignee = getPrimaryAssignee(task);
  const initialAssigneeUserId = primaryAssignee?.user?.id ?? '';
  const initialAssigneeName = getAssigneeName(task);
  const initialAssigneeAvatar = getAssigneeAvatar(task);
  const initialServiceTypeId = getAssigneeServiceTypeId(task);
  const initialServiceTypeLabel = primaryAssignee?.service_type?.label ?? '';
  const [createTaskAssignee] = useCreateTaskAssigneeMutation();
  const [updateTaskAssignee] = useUpdateTaskAssigneeMutation();

  const [description, setDescription] = useState(task.description ?? '');
  const [clientMode, setClientMode] = useState<ClientMode>(
    task.client?.company || task.client?.person ? 'client' : 'internal',
  );
  const [company, setCompany] = useState<CompanyOption | null>(
    task.client?.company
      ? {
          id: task.client.company.id,
          name: task.client.company.name,
        }
      : null,
  );
  const [person, setPerson] = useState<PersonOption | null>(
    task.client?.person
      ? {
          id: task.client.person.id,
          name: toPersonName(task.client.person),
          companyId: task.client.person.company?.id ?? null,
        }
      : null,
  );
  const [assigneeId, setAssigneeId] = useState(initialAssigneeUserId);
  const [taskAssigneeId, setTaskAssigneeId] = useState(
    primaryAssignee?.id ?? '',
  );
  const [serviceTypeId, setServiceTypeId] = useState(initialServiceTypeId);
  const [levelOfEffort, setLevelOfEffort] = useState<number | null>(
    primaryAssignee?.level_of_effort ?? null,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const clearError = (field: keyof FormErrors) => {
    setErrors((current) => {
      if (!current[field]) {
        return current;
      }

      return {
        ...current,
        [field]: undefined,
      };
    });
  };

  const saveTaskChanges = async (payload: UpdateTaskPayload) => {
    try {
      setIsSaving(true);
      setErrors({});
      await onUpdateTask({ taskId: task.id, payload });
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapBackendPath,
      });

      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors as FormErrors);
      }

      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  };

  const saveAssigneeChanges = async ({
    nextAssigneeId,
    nextServiceTypeId,
    nextLevelOfEffort,
  }: {
    nextAssigneeId: string;
    nextServiceTypeId: string;
    nextLevelOfEffort: number | null;
  }) => {
    if (!nextAssigneeId) {
      setErrors((current) => ({
        ...current,
        assignee_id: 'Assignee is required.',
      }));
      return;
    }

    try {
      setIsSaving(true);
      setErrors({});

      if (taskAssigneeId) {
        await updateTaskAssignee({
          taskAssigneeId,
          payload: {
            user_id: nextAssigneeId,
            service_type_id: nextServiceTypeId || null,
            level_of_effort: nextLevelOfEffort ?? 0,
          },
        }).unwrap();
        return;
      }

      const payload: CreateTaskAssigneePayload = {
        user_id: nextAssigneeId,
        service_type_id: nextServiceTypeId || null,
        level_of_effort: nextLevelOfEffort ?? 0,
      };
      const createdAssignment = await createTaskAssignee({
        taskId: task.id,
        payload,
      }).unwrap();

      const createdAssigneeId = createdAssignment?.data?.id ?? '';
      if (createdAssigneeId) {
        setTaskAssigneeId(createdAssigneeId);
      }
    } catch (error) {
      const backendErrors = (error as any)?.data?.errors;
      const mappedErrors = backendErrorsToFieldErrors(backendErrors, {
        mapPath: mapAssigneeBackendPath,
      });

      if (Object.keys(mappedErrors).length > 0) {
        setErrors(mappedErrors as FormErrors);
      }

      notify({ type: 'error', message: getErrorMessage(error) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.quickTaskSubRowForm}>
      <SmartInput
        type="textarea"
        name={`quick-task-description-${task.id}`}
        label="Description"
        value={description}
        error={errors.description}
        onChange={(nextValue) => {
          setDescription(nextValue);
          clearError('description');
        }}
        onBlur={() => {
          void saveTaskChanges({
            description: description.trim() || null,
          });
        }}
      />

      <SectionTabs
        sections={CONTEXT_SECTIONS.map((section) => ({
          id: section.id,
          label: section.label,
        }))}
        activeId={clientMode}
        onSectionChange={(nextValue) => {
          const nextMode = nextValue as ClientMode;
          setClientMode(nextMode);

          if (nextMode === 'internal') {
            setCompany(null);
            setPerson(null);
            clearError('company_id');
            clearError('person_id');

            void saveTaskChanges({
              company_contact_id: null,
              person_contact_id: null,
            });
          }
        }}
        aria-label="Task context"
      />

      {clientMode === 'client' ? (
        <CompanyPersonSelectFields
          company={company}
          person={person}
          onCompanyChange={(nextCompany, nextPerson) => {
            setCompany(nextCompany);
            setPerson(nextPerson);
            clearError('company_id');
            clearError('person_id');

            void saveTaskChanges({
              company_contact_id: nextCompany?.id ?? null,
              person_contact_id: nextPerson?.id ?? null,
            });
          }}
          onPersonChange={(nextPerson, nextCompany) => {
            setCompany(nextCompany);
            setPerson(nextPerson);
            clearError('company_id');
            clearError('person_id');

            void saveTaskChanges({
              company_contact_id: nextCompany?.id ?? null,
              person_contact_id: nextPerson?.id ?? null,
            });
          }}
          companyError={errors.company_id}
          personError={errors.person_id}
          companyLabel="Company"
          personLabel="Person"
          personPlaceholder="Optional person"
          layout="inline"
        />
      ) : null}

      <div className={styles.quickTaskSubRowForm__row}>
        <AssigneeSelect
          value={assigneeId}
          selectedLabel={
            assigneeId === initialAssigneeUserId
              ? initialAssigneeName
              : undefined
          }
          selectedAvatarSrc={
            assigneeId === initialAssigneeUserId
              ? initialAssigneeAvatar
              : undefined
          }
          error={errors.assignee_id}
          onChange={(nextValue) => {
            setAssigneeId(nextValue);
            clearError('assignee_id');
            clearError('service_type_id');
            clearError('level_of_effort');

            void saveAssigneeChanges({
              nextAssigneeId: nextValue,
              nextServiceTypeId: serviceTypeId,
              nextLevelOfEffort: levelOfEffort,
            });
          }}
        />

        <ServiceTypeSelect
          value={serviceTypeId}
          selectedLabel={
            serviceTypeId === initialServiceTypeId
              ? initialServiceTypeLabel
              : undefined
          }
          includeAllOption={false}
          onChange={(nextValue) => {
            setServiceTypeId(nextValue);
            clearError('service_type_id');

            void saveAssigneeChanges({
              nextAssigneeId: assigneeId,
              nextServiceTypeId: nextValue,
              nextLevelOfEffort: levelOfEffort,
            });
          }}
          name={`quick-task-service-type-${task.id}`}
          label="Service type"
          error={errors.service_type_id}
          size="medium"
        />

        <SmartInput
          type="number"
          name={`quick-task-level-of-effort-${task.id}`}
          label="LOE (hours)"
          value={levelOfEffort}
          min={0}
          step={0.1}
          error={errors.level_of_effort}
          onChange={(nextValue) => {
            setLevelOfEffort(nextValue);
            clearError('level_of_effort');
          }}
          onBlur={() => {
            void saveAssigneeChanges({
              nextAssigneeId: assigneeId,
              nextServiceTypeId: serviceTypeId,
              nextLevelOfEffort: levelOfEffort,
            });
          }}
        />
      </div>

      <p className={styles.quickTaskSubRowForm__status}>
        {errors.form ? errors.form : isSaving ? 'Saving changes...' : ''}
      </p>
    </div>
  );
}
